// Upload a file from the Files card (Record board, S14): drag in or choose one PDF, say what it is,
// and add it to the renewal. Markup from design/kit/components/Dialog, the kit's drop area (`drop`,
// `drop-ic`), the import file row (`imp-file`), the radio options (`ropts`, `ropt`, `rdot`) and the
// calm note (`anote calm`). The same size limit and PDF check as Upload PDF in step 1.
import { useRef, useState, type DragEvent, type RefObject } from 'react';
import type { Api, FileAdded, FileType, RenewalDetail } from '../auth/api';
import { Dialog } from '../shell/Dialog';
import { FileIcon, InfoIcon, UploadIcon } from '../shell/icons';
import {
  canSendFile,
  decPageNote,
  dropLine,
  dropTitle,
  fileTypeChoices,
  filesFootnote,
} from './files-lines';
import { fileSize, pickProblem, uploadError } from './upload-lines';

type Props = {
  api: Api;
  r: RenewalDetail;
  onClose: () => void;
  onAdded: (result: FileAdded, name: string) => void;
};

export function FileUploadDialog({ api, r, onClose, onAdded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState<FileType | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pickRef = useRef<HTMLInputElement>(null);

  function take(picked: File | undefined) {
    if (!picked) return;
    const problem = pickProblem(picked);
    setError(problem);
    setFile(problem ? null : picked);
  }

  async function send() {
    if (!file || !type) return;
    setBusy(true);
    setError(null);
    const res = await api.uploadFile(r.id, file, file.name, type);
    setBusy(false);
    if (res.ok) return onAdded(res.body, res.body.file?.fileName ?? file.name);
    setError(uploadError(res.status, res.error));
  }

  return (
    <FileUploadView
      subline={[r.insured, r.carrier ?? 'Carrier not in AMS360', r.line, r.policyNumber].join(
        ' | ',
      )}
      file={file}
      type={type}
      busy={busy}
      error={error}
      pickRef={pickRef}
      onPick={take}
      onRemove={() => setFile(null)}
      onType={setType}
      onSend={() => void send()}
      onClose={onClose}
    />
  );
}

/** The popup as it looks for a given file, type and state (no state of its own). */
export function FileUploadView({
  subline,
  file,
  type,
  busy,
  error,
  pickRef,
  onPick,
  onRemove,
  onType,
  onSend,
  onClose,
}: {
  subline: string;
  file: { name: string; size: number } | null;
  type: FileType | null;
  busy: boolean;
  error: string | null;
  pickRef?: RefObject<HTMLInputElement | null>;
  onPick: (file: File | undefined) => void;
  onRemove: () => void;
  onType: (type: FileType) => void;
  onSend: () => void;
  onClose: () => void;
}) {
  function onDrop(e: DragEvent) {
    e.preventDefault();
    // One file: the first one dropped.
    onPick(e.dataTransfer.files[0]);
  }
  return (
    <Dialog
      id="fup-title"
      title="Upload a file"
      subline={subline}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary ml-auto"
            disabled={!canSendFile(file, type) || busy}
            onClick={onSend}
          >
            {busy && <span className="spin" />}
            Upload PDF
          </button>
        </>
      }
    >
      {file ? (
        <div className="imp-file">
          <FileIcon className="ico s14" />
          <span className="t13 ink ell">{file.name}</span>
          <span className="t12 muted">{fileSize(file.size)}</span>
          <button type="button" className="linkbtn" onClick={onRemove}>
            Remove
          </button>
        </div>
      ) : (
        <div className="drop" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
          <span className="drop-ic">
            <UploadIcon className="ico s14" />
          </span>
          <span className="t13 w5 ink">{dropTitle}</span>
          <span className="t12 muted">{dropLine}</span>
          <button type="button" className="btn btn-sm" onClick={() => pickRef?.current?.click()}>
            Choose file
          </button>
        </div>
      )}
      <input
        ref={pickRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <div>
        <div id="fup-type" className="flab">
          Document type <span className="req">*</span>
        </div>
        <div className="ropts" role="radiogroup" aria-labelledby="fup-type">
          {fileTypeChoices.map((c) => (
            <button
              key={c.type}
              type="button"
              role="radio"
              className={type === c.type ? 'ropt on' : 'ropt'}
              aria-checked={type === c.type}
              onClick={() => onType(c.type)}
            >
              <span className="rdot" />
              <span className="t13 ink">{c.label}</span>
            </button>
          ))}
        </div>
      </div>
      {type === 'dec_page' && (
        <div className="anote calm">
          <InfoIcon className="ico s14" />
          <span>{decPageNote}</span>
        </div>
      )}
      {error && (
        <p className="ferr m0" role="alert">
          {error}
        </p>
      )}
      <p className="fnote">{filesFootnote}</p>
    </Dialog>
  );
}
